import _extends from "@babel/runtime/helpers/extends";
import classNames from "classnames/dedupe";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Label } from "../label";

/**
 * TextInput component
 * @typedef {object} TextInputProps
 * @prop {string} [id]
 * @prop {string} [name]
 * @prop {string} [type]
 * @prop {string} [value]
 * @prop {string} [placeholder]
 * @prop {any} [className]
 * @prop {string} [label]
 * @prop {boolean} [valid]
 * @prop {boolean} [disabled]
 * @prop {boolean} [readOnly]
 * @prop {import("react").ChangeEventHandler<HTMLInputElement>} [onChange]
 * @prop {import("react").FocusEventHandler<HTMLInputElement>} [onFocus]
 * @prop {import("react").FocusEventHandler<HTMLInputElement>} [onBlur]
 * @prop {import("react").ForwardRefExoticComponent} [Input]
 */

export const TextInput = /*#__PURE__*/React.forwardRef(
/**
 * TextInput component
 * @param {TextInputProps & { children?: any } & React.HTMLAttributes<HTMLInputElement>} props
 * @param {import("react").Ref<HTMLInputElement>} ref
 * @returns {JSX.Element}
 */
function TextInput({
  className,
  label,
  valid,
  disabled,
  children,
  readOnly,
  onChange,
  onFocus,
  onBlur,
  Input,
  ...props
}, ref) {
  const [isActive, setIsActive] = useState(false);
  const activeClasses = classNames("border-en-gray-400", {
    "hover:border-en-gray-800 focus:border-en-yellow-700": !readOnly
  });
  const invalidClasses = "border-en-red focus:border-en-red hover:border-en-red";
  const disabledClasses = "border-en-gray-600 bg-en-gray-100";
  const hasValidation = () => ![null, undefined].includes(valid);
  const validationStyle = () => {
    if (hasValidation()) {
      return valid ? isActive ? "" : activeClasses : invalidClasses;
    }
  };
  const textboxClasses = classNames(["border rounded-md p-2 caret-en-yellow-700 w-full placeholder-en-gray-300", "focus:outline-none"],
  // don't apply activeStyle if has valid or disabled
  isActive && (hasValidation() ? valid : true) && !disabled && activeClasses,
  // don't apply disabledStyle if has valid
  !hasValidation() && disabled && disabledClasses, validationStyle());
  const onClickHandler = e => {
    e?.stopPropagation();
    typeof props.onClick === "function" && props.onClick(e);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, label && /*#__PURE__*/React.createElement(Label, {
    className: "mb-1"
  }, label), /*#__PURE__*/React.createElement(Input, _extends({}, props, {
    className: textboxClasses,
    onClick: onClickHandler,
    onChange: e => {
      typeof onChange === "function" && onChange(e);
      setIsActive(false);
    },
    onFocus: e => {
      typeof onFocus === "function" && onFocus(e);
      setIsActive(true);
    },
    onBlur: onBlur,
    disabled: disabled,
    ref: ref
  })), children);
});
TextInput.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  valid: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.any,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  readOnly: PropTypes.bool,
  Input: PropTypes.any,
  placeholder: PropTypes.string
};
TextInput.defaultProps = {
  type: "text",
  Input: /*#__PURE__*/React.forwardRef(function Input(props, ref) {
    return /*#__PURE__*/React.createElement("input", _extends({
      ref: ref
    }, props));
  })
};